import engineFixtureTour, { ENGINE_FIXTURE_TOUR_ID } from './definitions/engineFixtureTour';
import welcomeTour, { WELCOME_TOUR_ID } from './definitions/welcomeTour';
import { normalizeTour, validateTour } from './tourSchema';

/**
 * THE LOADER (KPR-611): the one way a tour reaches the engine.
 *
 *   const tour = loadTour('engine-fixture');   // a frozen tour, or null
 *
 * Tours are typed definition files shipped and reviewed with the UI they point
 * at. Going through one function leaves room for a second source later (a tour
 * stored as a keepr item, for ad hoc training) without the engine changing:
 * nothing else would have to learn where a definition came from. That source
 * is NOT built (KPE-024 "Out of scope"), and the sources allowed today are
 * exactly two, pinned by `__tests__/loadTour.test.js`:
 *
 *   'welcome'         the welcome tour (KPR-613)
 *   'engine-fixture'  the engine's own test and screenshot tour
 *
 * An unknown id and a definition that does not validate both answer `null`,
 * never a throw: a tour that cannot load must not break the page that offered
 * it. The second is also an error in the console, because it is a bug, and the
 * tests validate every registered tour so it cannot ship.
 */
export const ALLOWED_TOUR_IDS = Object.freeze([WELCOME_TOUR_ID, ENGINE_FIXTURE_TOUR_ID]);

// id -> () => definition. A function so a definition file could one day be a
// lazy chunk without the loader's callers changing.
const SOURCES = {
	[WELCOME_TOUR_ID]: () => welcomeTour,
	[ENGINE_FIXTURE_TOUR_ID]: () => engineFixtureTour,
};

const cache = new Map();

/** The ids of every registered tour. */
export const listTourIds = () => Object.keys(SOURCES);

/**
 * @param {string} id
 * @returns {Readonly<import('./tourSchema').Tour> | null}
 */
export function loadTour(id) {
	if (typeof id !== 'string' || !Object.prototype.hasOwnProperty.call(SOURCES, id)) return null;
	if (cache.has(id)) return cache.get(id);
	const definition = SOURCES[id]();
	const problems = validateTour(definition);
	if (problems.length > 0) {
		if (process.env.NODE_ENV !== 'production') console.error(`[tour] "${id}" did not load:\n- ${problems.join('\n- ')}`);
		return null;
	}
	if (definition.id !== id) {
		if (process.env.NODE_ENV !== 'production') console.error(`[tour] "${id}" is registered for a definition whose id is "${definition.id}".`);
		return null;
	}
	const tour = normalizeTour(definition);
	cache.set(id, tour);
	return tour;
}
